import { AxiosResponse } from 'axios';
import api from './api';
import Template from '../models/Template/Template';
import { PaginationLinks, PaginationMeta, PaginationMetaLink } from '../models/Paginator';

type TemplateCreateRequest = Omit<Template, 'id'>;

interface InternalTemplate extends Omit<Template, 'template'> {
  template: string;
}

interface GetAllTemplatesResponse {
  data: InternalTemplate[]
  meta: PaginationMeta
  links: PaginationLinks
}

export interface GetAllTemplatesResult {
  data: Template[]
  meta: PaginationMeta
  links: PaginationLinks
}

export const getAllTemplates = async (page?: number, perPage?: number, searchText?: string): Promise<GetAllTemplatesResult> => {
  let url = 'v1/templates';

  const obj = new URLSearchParams();

  if (page != null) {
    obj.append('page', `${page}`);
  }

  if (perPage != null) {
    obj.append('per_page', `${perPage}`);
  }

  if (searchText != null) {
    obj.append('search_term', `${searchText}`);
  }

  url += `?${obj.toString()}`;

  const result = await (await api.get<GetAllTemplatesResponse>(url)).data;
  return {
    data: result.data ? result.data.map((t) => ({ ...t, template: JSON.parse(t.template) })) : [],
    links: result.links,
    meta: result.meta,
  };
};

interface GetPageTemplatesByCategoryResponse {
  templateList: InternalTemplate[],
  templateListWithPagination: {
    data: InternalTemplate[],
    links: PaginationMetaLink[],
    current_page: number,
    total: number,
  }
}

export interface GetPageTemplatesByCategoryResult {
  data: Template[]
  currentPage: number
  links: PaginationMetaLink[]
  total: number
}

export const getTemplates = async (categoryId: string, page?: number, searchText?: string): Promise<GetPageTemplatesByCategoryResult> => {
  let url = `v1/page_template_category/${categoryId}/templates`;

  const obj = new URLSearchParams();

  if (page != null) {
    obj.append('page', `${page}`);
  }

  if (searchText != null) {
    obj.append('search_term', `${searchText}`);
  }

  url += `?${obj.toString()}`;

  const result = await (await api.get<GetPageTemplatesByCategoryResponse>(url)).data;
  const tList = result.templateListWithPagination.data ? result.templateListWithPagination.data.map((t) => ({ ...t, template: JSON.parse(t.template) })) : [];
  return {
    data: tList,
    total: result.templateListWithPagination.total,
    links: result.templateListWithPagination.links,
    currentPage: result.templateListWithPagination.current_page,
  };
};

export const getTemplate = async (id: string): Promise<AxiosResponse<{ data: Template }>> => api.get(`v1/templates/${id}`);

export const post = async (data: TemplateCreateRequest): Promise<AxiosResponse<string>> => api.post<string>('v1/templates/create', { ...data, template: JSON.stringify(data.template) });

export const put = async (data: Template) => api.post(`v1/templates/${data.id}/update`, { ...data, template: JSON.stringify(data.template) });

export const destroy = async (id: string) => api.post(`v1/templates/${id}/delete`, {});

export default {
  getAllTemplates,
  getTemplates,
  post,
  put,
  destroy,
};
